/**
 * CameraDirector — Camera pan/zoom abstraction for cinematic sequences.
 * Resolves named targets (base, spawn, path) to world coordinates
 * and wraps Phaser camera methods with completion callbacks via EventBus.
 */
import Phaser from 'phaser';
import { BASE_POSITIONS, SPAWN_POSITIONS, PATHS } from '@/map/MapData';
import { GAME_WIDTH, GAME_HEIGHT, TILE_SIZE, TileType } from '@/config/GameConfig';
import { MAP_GRID } from '@/map/MapData';
import { GOD } from '@/config/GodMode';
import { EventBus } from '@/events/EventBus';
import { CinematicEvents } from './CinematicEvents';

type NamedTarget = 'base' | 'spawn' | 'path';

/** Compute the centroid of an array of positions */
function centroid(positions: { x: number; y: number }[]): { x: number; y: number } {
  if (positions.length === 0) return { x: GAME_WIDTH / 2, y: GAME_HEIGHT / 2 };
  const sum = positions.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
  return { x: sum.x / positions.length, y: sum.y / positions.length };
}

/** Find the first PLATFORM tile position (world coords) for demo robot placement */
export function findFirstPlacementTile(): { x: number; y: number } | null {
  const rows = MAP_GRID.length;
  for (let r = 0; r < rows; r++) {
    const cols = MAP_GRID[r].length;
    for (let c = 0; c < cols; c++) {
      if (MAP_GRID[r][c] === TileType.PLACEMENT) {
        return { x: c * TILE_SIZE + TILE_SIZE / 2, y: r * TILE_SIZE + TILE_SIZE / 2 };
      }
    }
  }
  return null;
}

export class CameraDirector {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Resolve a named target to world coordinates */
  resolveTarget(target: { x: number; y: number } | NamedTarget): { x: number; y: number } {
    if (typeof target === 'object') return target;
    switch (target) {
      case 'base':  return centroid(BASE_POSITIONS);
      case 'spawn': return centroid(SPAWN_POSITIONS);
      case 'path': {
        if (PATHS.length > 0 && PATHS[0].length > 0) {
          const mid = Math.floor(PATHS[0].length / 2);
          return PATHS[0][mid];
        }
        return { x: GAME_WIDTH / 2, y: GAME_HEIGHT / 2 };
      }
      default: return { x: GAME_WIDTH / 2, y: GAME_HEIGHT / 2 };
    }
  }

  /** Stop any in-flight pan/zoom effects so they don't conflict with the next one */
  private resetCameraEffects(): void {
    const cam = this.scene.cameras.main;
    cam.stopFollow();
    (cam as any).panEffect?.reset();
    (cam as any).zoomEffect?.reset();
  }

  /** Pan camera to target and optionally zoom. Emits CAMERA_PAN_DONE on completion. */
  panTo(
    target: { x: number; y: number } | NamedTarget,
    zoom?: number,
    durationMs?: number,
  ): void {
    const cam = this.scene.cameras.main;
    this.resetCameraEffects();

    const pos = this.resolveTarget(target);
    const dur = durationMs ?? GOD.cinematicPanTime ?? 800;
    const ease = 'Sine.easeInOut';

    cam.pan(pos.x, pos.y, dur, ease);
    if (zoom != null) {
      cam.zoomTo(zoom, dur, ease);
    }

    cam.once('camerapancomplete', () => {
      EventBus.emit(CinematicEvents.CAMERA_PAN_DONE);
    });
  }

  /** Reset camera to map center with default zoom */
  reset(durationMs = 600): void {
    this.resetCameraEffects();
    const cam = this.scene.cameras.main;
    cam.pan(GAME_WIDTH / 2, GAME_HEIGHT / 2, durationMs, 'Sine.easeInOut');
    cam.zoomTo(1, durationMs, 'Sine.easeInOut');
  }

  destroy(): void {
    // No persistent listeners to clean up
  }
}
